// Time arithmetic of a schedule (R-S6, decision 036): the event's rules, shifting the slots after a changed one, automatic breaks.
// Pure functions: the server commands apply them in a transaction, the editor shows the same result before saving.

/** «ЧЧ:ММ» → minutes of the day; anything else is NaN. */
export const toMin = (v: unknown): number => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(v ?? '')); return m && Number(m[1]) < 24 && Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : NaN; };
export const hhmm = (m: number): string => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** The length of a slot, the same bounds for the editor and the server: whole minutes, 5 to 720. */
export const DURATION = { min: 5, max: 720 } as const;
export const DURATION_TEXT = `Длительность — целое число минут от ${DURATION.min} до ${DURATION.max}`;
export const validDuration = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= DURATION.min && v <= DURATION.max;
/** «Минут» as typed: a valid length, or null — an empty field or «abc» is not sent as 0 or NaN (audit 31.10, P2-11). */
export function parseDuration(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return validDuration(n) ? n : null;
}

/**
 * The event's schedule rules (ITConf practice: a break every two hours, a changeover between talks, working hours of the day).
 * `breakAfter` 0 turns automatic breaks off; `transition` is the minimum gap between two slots of a room and the time a speaker
 * needs to move between rooms.
 */
export interface ScheduleRules { dayStart: number; dayEnd: number; transition: number; breakAfter: number; breakLength: number; breakTitle: string }
export const RULE_DEFAULTS: ScheduleRules = { dayStart: 9 * 60, dayEnd: 20 * 60, transition: 10, breakAfter: 120, breakLength: 20, breakTitle: 'Кофе-брейк' };

/** An empty value is «not set» (the default), not zero. */
const clamp = (v: unknown, lo: number, hi: number, dflt: number) => { if (v === '' || v == null) return dflt; const n = Math.round(Number(v)); return Number.isFinite(n) && n >= lo && n <= hi ? n : dflt; };

/** Rules from what the event stores (JSON) or from input: unknown or out-of-range values fall back to the defaults. */
export function readRules(raw: unknown): ScheduleRules {
  let o: Record<string, unknown> = {};
  if (typeof raw === 'string' && raw) { try { o = JSON.parse(raw) as Record<string, unknown>; } catch { o = {}; } } else if (raw && typeof raw === 'object') o = raw as Record<string, unknown>;
  const time = (v: unknown, d: number) => (typeof v === 'string' ? (Number.isNaN(toMin(v)) ? d : toMin(v)) : clamp(v, 0, 24 * 60, d));
  const dayStart = time(o.dayStart, RULE_DEFAULTS.dayStart);
  const dayEnd = time(o.dayEnd, RULE_DEFAULTS.dayEnd);
  return {
    dayStart, dayEnd: dayEnd > dayStart ? dayEnd : RULE_DEFAULTS.dayEnd > dayStart ? RULE_DEFAULTS.dayEnd : 24 * 60,
    transition: clamp(o.transition, 0, 60, RULE_DEFAULTS.transition),
    breakAfter: clamp(o.breakAfter, 0, 600, RULE_DEFAULTS.breakAfter),
    breakLength: clamp(o.breakLength, 5, 180, RULE_DEFAULTS.breakLength),
    breakTitle: typeof o.breakTitle === 'string' && o.breakTitle.trim() ? o.breakTitle.trim().slice(0, 80) : RULE_DEFAULTS.breakTitle,
  };
}

/** Slot kinds that are a pause: a lane that reaches one starts counting its run anew. */
export const PAUSE_KINDS = ['break', 'lunch', 'networking'];

/** One slot of a lane (a room on a day) as the arithmetic needs it. */
export interface LaneSlot { id: string; start: number; duration: number; kind: string | null; locked?: boolean; auto?: boolean }
export interface Move { id: string; start: number }

const byStart = (a: LaneSlot, b: LaneSlot) => a.start - b.start || a.id.localeCompare(b.id);

/**
 * The slots after a changed one follow it (ITConf: a talk that grew pushes the rest of the room): every later slot of the lane moves
 * by how much the end of the changed slot moved, up to the first slot locked by hand — it and what follows it stay.
 * `before` is the changed slot as it was, `after` as it is now.
 */
export function shiftFollowing(lane: LaneSlot[], before: { id: string; start: number; duration: number }, after: { start: number; duration: number }): Move[] {
  const delta = after.start + after.duration - (before.start + before.duration);
  if (!delta) return [];
  const out: Move[] = [];
  for (const s of [...lane].sort(byStart)) {
    if (s.id === before.id || s.start <= before.start) continue;
    if (s.locked) break;
    out.push({ id: s.id, start: s.start + delta });
  }
  return out;
}

/** A break the rules ask for: where it goes in the lane. */
export interface PlannedBreak { start: number; duration: number }

/**
 * Automatic breaks of one lane (ITConf «каждые два часа — перерыв»): the lane runs without a pause at most `breakAfter` minutes;
 * before a slot that would run over, a break of `breakLength` goes in and the rest of the lane moves later. Two slots of the lane
 * keep at least `transition` minutes between them. A pause slot, or a gap as long as a break, starts the count again.
 * Locked slots never move; automatic breaks of an earlier pass are left out of `lane` and given as `removed`.
 */
export function planBreaks(lane: LaneSlot[], r: ScheduleRules, removed: PlannedBreak[] = []): { breaks: PlannedBreak[]; moves: Move[] } {
  const breaks: PlannedBreak[] = [];
  const moves: Move[] = [];
  let shift = 0, run = 0;
  let prevEnd: number | null = null;
  // The breaks of the last pass are taken out first and their time closed up: a second pass gives the same lane, not a gap that
  // reads as a pause (the pass is repeatable).
  const closed = (s: LaneSlot) => (s.locked ? s.start : s.start - removed.filter((b) => b.start + b.duration <= s.start).reduce((n, b) => n + b.duration, 0));
  for (const s of [...lane].sort(byStart)) {
    let start = s.locked ? s.start : closed(s) + shift;
    const pause = !!s.kind && PAUSE_KINDS.includes(s.kind);
    if (prevEnd !== null && start - prevEnd >= r.breakLength) run = 0;
    if (!pause && prevEnd !== null) {
      if (r.breakAfter > 0 && run > 0 && run + s.duration > r.breakAfter) {
        breaks.push({ start: prevEnd, duration: r.breakLength });
        prevEnd += r.breakLength;
        run = 0;
        if (!s.locked && start < prevEnd) { shift += prevEnd - start; start = prevEnd; }
      } else if (!s.locked && start < prevEnd + r.transition) { shift += prevEnd + r.transition - start; start = prevEnd + r.transition; }
    }
    if (start !== s.start) moves.push({ id: s.id, start });
    run = pause ? 0 : run + s.duration;
    prevEnd = Math.max(prevEnd ?? 0, start + s.duration);
  }
  return { breaks, moves };
}
